import React, { useState } from 'react';
import { questionGenerator } from '../../services/questionGenerator';

const InteractiveStudyModeTailwind = ({ content, lineIndex, onContentUpdate, courseColor }) => {
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [userAnswer, setUserAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [result, setResult] = useState(null);
  const [needsRephrase, setNeedsRephrase] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [attemptCount, setAttemptCount] = useState(0);
  const [showingExplanation, setShowingExplanation] = useState(false);
  const [currentExplanation, setCurrentExplanation] = useState(null);
  const [questionCount, setQuestionCount] = useState(0);
  const [explanationCount, setExplanationCount] = useState(0);

  const generateQuestion = async () => {
    if (loading) return;
    
    setLoading(true);
    setResult(null);
    setUserAnswer('');
    setShowingExplanation(false);
    setCurrentExplanation(null);
    
    try {
      const question = await questionGenerator.generateQuestion(
        content, 
        'Educational quiz question'
      );
      
      setCurrentQuestion(question.question || `Explain: "${content.slice(0, 60)}..."`);
      setQuestionCount(prev => prev + 1);
    } catch (error) {
      console.error('Error generating question:', error);
      setCurrentQuestion(`Explain the key concept in: "${content.slice(0, 50)}..."`);
      setQuestionCount(prev => prev + 1);
    } finally {
      setLoading(false);
    }
  };

  const generateExplanation = async () => {
    if (loading) return;
    
    setLoading(true);
    setResult(null);
    setUserAnswer('');
    setCurrentQuestion('');
    setShowingExplanation(true);
    
    try {
      const explanation = await questionGenerator.generateExplanation(
        content,
        'Educational explanation with real-world examples'
      );
      
      setCurrentExplanation(explanation);
      setExplanationCount(prev => prev + 1);
    } catch (error) {
      console.error('Error generating explanation:', error);
      setCurrentExplanation({
        title: "Key Concept",
        explanation: `This content explains: ${content}`,
        realWorldExample: "This concept is used in various real-world applications across technology and science.",
        whyItMatters: "Understanding this helps build foundational knowledge for more advanced topics."
      });
      setExplanationCount(prev => prev + 1);
    } finally {
      setLoading(false);
    }
  };

  const submitAnswer = async () => {
    if (!userAnswer.trim() || evaluating) return;
    
    setEvaluating(true);
    setNeedsRephrase(false);
    setSuggestions([]);
    
    try {
      const contentKey = `${content.slice(0, 50)}-${lineIndex}`;
      
      const evaluation = await questionGenerator.evaluateAnswer(
        contentKey,
        currentQuestion,
        userAnswer,
        content
      );
      
      if (evaluation.needsRephrase) {
        setNeedsRephrase(true);
        setSuggestions(evaluation.suggestions || []);
        setResult({
          correct: false,
          message: evaluation.message,
          needsRephrase: true
        });
      } else {
        setResult({
          correct: evaluation.correct,
          message: evaluation.message,
          explanation: evaluation.explanation,
          context: evaluation.context
        });
        setAttemptCount(evaluation.context?.attempts || 1);
      }
    } catch (error) {
      console.error('Error evaluating answer:', error);
      setResult({
        correct: false,
        message: 'Error evaluating your answer. Please try again.',
        explanation: 'There was a technical issue with the evaluation.'
      });
    } finally {
      setEvaluating(false);
    }
  };

  const resetQuestion = () => {
    setCurrentQuestion('');
    setUserAnswer('');
    setResult(null);
    setNeedsRephrase(false);
    setSuggestions([]);
    setAttemptCount(0);
    setShowingExplanation(false);
    setCurrentExplanation(null);
    
    const contentKey = `${content.slice(0, 50)}-${lineIndex}`;
    questionGenerator.clearContext(contentKey);
  };

  const tryAgain = () => {
    setUserAnswer('');
    setResult(null);
    setNeedsRephrase(false);
    setSuggestions([]);
  };

  return (
    <div className="py-2">
      {/* Main Interactive Row */}
      <div className={`flex items-center gap-4 min-h-[60px] p-3 rounded-lg transition-all duration-300 ${
        currentQuestion || showingExplanation 
          ? 'bg-gray-50 border border-gray-200' 
          : 'bg-transparent border border-transparent hover:bg-gray-25'
      }`}>
        
        {/* 1. Content */}
        <div className="flex-shrink-0 w-80 pr-4">
          <p className="text-sm text-gray-900 leading-relaxed">
            {content}
          </p>
        </div>

        {/* 2. Question/Explanation */}
        <div className="flex-1 pr-4">
          {loading ? (
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
              <span className="text-sm text-gray-500">Generating...</span>
            </div>
          ) : showingExplanation && currentExplanation ? (
            <div>
              <p className="text-sm font-semibold text-blue-600 mb-1">
                📚 {currentExplanation.title}
              </p>
              <p className="text-sm text-gray-700 leading-relaxed">
                {currentExplanation.keyTakeaway || currentExplanation.explanation}
              </p>
            </div>
          ) : currentQuestion ? (
            <p className="text-sm font-medium text-blue-600">
              {currentQuestion}
            </p>
          ) : (
            <p className="text-sm text-gray-400 italic">
              Click Question or What's That to interact with this content
            </p>
          )}
          
          {/* Result indicator */}
          {result && (
            <div className="flex items-center gap-2 mt-2">
              {result.needsRephrase ? (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  Please rephrase
                </span>
              ) : result.correct ? (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  ✓ Correct!
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  ✗ Try again
                </span>
              )}
              {attemptCount > 1 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border">
                  #{attemptCount}
                </span>
              )}
            </div>
          )}
          
          {/* Suggestions */}
          {needsRephrase && suggestions.length > 0 && (
            <div className="mt-2">
              <p className="text-xs text-gray-500 mb-1">Suggestions:</p>
              <div className="flex flex-wrap gap-1">
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => setUserAnswer(suggestion)}
                    className="inline-flex items-center px-2 py-1 text-xs border border-blue-300 text-blue-700 rounded-md hover:bg-blue-50 transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 3. Answer Field */}
        <div className="flex-shrink-0 w-64 pr-2">
          <textarea
            className="w-full p-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            placeholder={showingExplanation ? "Explanation mode" : "Your answer..."}
            value={userAnswer}
            onChange={(e) => setUserAnswer(e.target.value)}
            disabled={!currentQuestion || showingExplanation}
            rows={2}
          />
        </div>

        {/* 4. Question Button */}
        <div className="flex-shrink-0 relative">
          <button
            onClick={generateQuestion}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-w-[90px]"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Question
          </button>
          {questionCount > 0 && (
            <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-blue-600 rounded-full">
              {questionCount}
            </span>
          )}
        </div>

        {/* 5. Submit/What's That Button */}
        <div className="flex-shrink-0 relative">
          {currentQuestion && !showingExplanation ? (
            <button
              onClick={submitAnswer}
              disabled={!currentQuestion || !userAnswer.trim() || loading || evaluating}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-w-[90px]"
            >
              {evaluating ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              ) : (
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              )}
              {evaluating ? 'Checking...' : needsRephrase ? 'Retry' : 'Submit'}
            </button>
          ) : (
            <button
              onClick={generateExplanation}
              disabled={loading}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-500 border border-blue-300 rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-w-[110px]"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              What's that?
            </button>
          )}
          {explanationCount > 0 && (
            <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-blue-400 rounded-full">
              {explanationCount}
            </span>
          )}
        </div>

        {/* Additional Buttons */}
        {result && !result.needsRephrase && !result.correct && (
          <button
            onClick={tryAgain}
            className="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50 transition-colors"
          >
            Try Again
          </button>
        )}

        <button
          onClick={resetQuestion}
          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          title="Reset"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      {/* Detailed Explanation Section */}
      {showingExplanation && currentExplanation && (
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="text-base font-semibold text-blue-900 mb-3">
            📚 {currentExplanation.title}
          </h4>
          
          <p className="text-sm text-gray-700 mb-3 leading-relaxed">
            {currentExplanation.explanation}
          </p>
          
          <div className="text-sm text-gray-600 mb-2 leading-relaxed italic">
            <strong>🌍 Real-world example:</strong> {currentExplanation.realWorldExample}
          </div>
          
          <div className="text-sm text-gray-600 leading-relaxed">
            <strong>💡 Why it matters:</strong> {currentExplanation.whyItMatters}
          </div>
        </div>
      )}
      
      {/* Answer Explanation for Wrong Answers */}
      {result && result.explanation && !result.correct && !result.needsRephrase && !showingExplanation && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="text-sm text-gray-700 leading-relaxed">
            <strong>💡 Explanation:</strong> {result.explanation}
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractiveStudyModeTailwind;